import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import type { LibraryAsset } from "./library";

import { useApiClient } from "./ApiContext";
import { unwrap } from "./client";
import { queryKeys } from "./hooks";

/**
 * WHICH Summary prompt an asset is, and which ones a clinician may author (#863).
 *
 * Every name here is aliased from the generated contract rather than declared,
 * exactly as `library.ts` does it: the service owns this vocabulary, and a shape
 * that drifts from the contract must stop compiling rather than stop working.
 */
export type SummaryTarget = components["schemas"]["SummaryTarget"];
export type SummarySectionTarget =
  components["schemas"]["SummarySectionTarget"];
export type SummaryDiagnosticTarget =
  components["schemas"]["SummaryDiagnosticTarget"];
export type SummaryTargetCatalog =
  components["schemas"]["SummaryTargetCatalog"];
export type SummarySectionOption =
  components["schemas"]["SummarySectionOption"];
export type SummaryDiagnosticGroupOption =
  components["schemas"]["SummaryDiagnosticGroupOption"];
export type SummaryDiagnosticEvaluationOption =
  components["schemas"]["SummaryDiagnosticEvaluationOption"];
export type SummaryDiagnosticCatalogSource =
  components["schemas"]["SummaryDiagnosticCatalogSource"];

/**
 * A library asset that IS a Summary prompt.
 *
 * `LibraryAsset` is discriminated on `assetType` and only this member carries
 * `summaryTarget`, which is the contract being precise rather than an
 * inconvenience: a surface that reads a target has to have established it is
 * looking at a Summary prompt, and this alias is how it says so once.
 */
export type SummaryPromptAsset = Extract<
  LibraryAsset,
  { assetType: "summary_prompt" }
>;

/**
 * The server-enumerated Summary targets a clinician may select.
 *
 * WHY IT IS FETCHED AND NOT A CONSTANT. `library.py`'s own preamble forbids a
 * second statement of what the service accepts, and #862's committed contract
 * forbids a free-form nested path. A browser that hardcoded the seven flat
 * section names would be that second manifest — free to drift the day the pinned
 * engine gains or loses a literal — and the diagnostic groups are not knowable
 * here at all: they come from the mounted base pack's own registration, which
 * moves with a pack pin.
 *
 * SAME CACHE DISCIPLINE AS THE PART MANIFEST. The key names no asset, so every
 * surface that needs the vocabulary reads one cache entry, and this read
 * describes the SERVICE rather than any asset: it must never block or re-seed an
 * editor that already has content in hand.
 *
 * IT NEVER 503s BY CONTRACT. The flat half is served whatever the deployment's
 * pack mount is doing, and `diagnosticSource` says whether the nested half could
 * be read — so a failure of this query is a failure of the READ, and the
 * degraded pack mount is a value inside a successful one. The two are different
 * states and the create dialog says different things about them.
 */
export function useSummaryTargetCatalogQuery(): UseQueryResult<SummaryTargetCatalog> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.summaryTargetCatalog,
    async queryFn() {
      return unwrap(await client.GET("/v1/library/summary-targets", {}));
    },
  });
}
